import React, { useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import SchoolSidebar from './SchoolSidebar';
import { useContentSetting } from '../../Hooks/ContentSetting';
import { useGodMode } from '../../Hooks/GodModeContext';
import GodModeBanner from '../../components/GodModeBanner';
import { IconButton, Box } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationBell from '../../components/NotificationBell';
import { bgGradient } from './schoolPortalTheme';
import { SchoolProvider } from '../../Hooks/SchoolContext';
import SessionJoinPrompt from '../../components/SessionJoinPrompt';
import InstructorOnboardingGuard from '../../components/InstructorOnboardingGuard';

export default function SchoolPortalLayout() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const { userInfo } = useContentSetting();
    const { isGodMode, impersonatedSchool } = useGodMode();

    if (!localStorage.getItem('token')) return <Navigate to="/login" />;

    const isXairAdmin = userInfo?.type === 'xair_admin' || userInfo?.role === 'xair_admin';
    if (isXairAdmin && !isGodMode) return <Navigate to="/dashboard" />;
    if (isXairAdmin && isGodMode && !impersonatedSchool) return <Navigate to="/flight-schools" />;

    return (
        <SchoolProvider>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: bgGradient }}>
                {isGodMode && <GodModeBanner />}
                {/* Every page, not just Teaching — see the component's own note. */}
                <SessionJoinPrompt />
                <Box sx={{ display: 'flex', flex: 1 }}>
                    {/* Mobile hamburger */}
                    <IconButton
                        onClick={() => setMobileOpen(!mobileOpen)}
                        sx={{
                            display: { xs: 'flex', sm: 'none' },
                            position: 'fixed',
                            top: 14,
                            left: 14,
                            zIndex: 1200,
                            background: '#fff',
                            border: '1px solid rgba(26,26,26,0.12)',
                            borderRadius: '999px',
                            padding: '9px',
                            color: '#1a1a1a',
                            boxShadow: '0 1px 3px rgba(26,26,26,0.05), 0 0 0 1px rgba(26,26,26,0.06)',
                        }}
                    >
                        <MenuIcon sx={{ fontSize: 18 }} />
                    </IconButton>

                    {/* The bell sits in the chrome, not on a page, so a request
                        is visible from wherever the instructor happens to be —
                        a booking request that expires unseen looks to the
                        student exactly like being ignored. */}
                    <Box sx={{ position: 'fixed', top: 10, right: 14, zIndex: 1200 }}>
                        <NotificationBell />
                    </Box>

                    <SchoolSidebar handleDrawerToggle={() => setMobileOpen(!mobileOpen)} mobileOpen={mobileOpen} />

                    <Box
                        component="main"
                        sx={{
                            flexGrow: 1,
                            overflow: 'auto',
                            minHeight: '100vh',
                            background: bgGradient,
                            // The hamburger is position:fixed at top-left, so on a phone it
                            // sits ON TOP of whatever the page renders first — every portal
                            // page's title was showing as "...ors" with the button over it.
                            // Reserving the space here fixes it once, for every page, rather
                            // than each page remembering to indent its own heading.
                            pt: { xs: '52px', sm: 0 },
                        }}
                    >
                        <InstructorOnboardingGuard>
                            <Outlet />
                        </InstructorOnboardingGuard>
                    </Box>
                </Box>
            </Box>
        </SchoolProvider>
    );
}
